/** @format */

import { useEffect } from "react";
import toast from "react-hot-toast";
import { useTranslation } from "react-i18next";
import { MdOutlineKeyboardArrowRight } from "react-icons/md";
import { useDispatch, useSelector } from "react-redux";
import { Link, useNavigate } from "react-router-dom";
import FadeLoader from "react-spinners/FadeLoader";
import Footer from "../components/Footer";
import Headers from "../components/Headers";
import {
  delete_card_product,
  get_card_products,
  messageClear,
} from "../store/reducers/cardReducers";
import { useCategories } from "../utils/Categories";
import QuantityInput from "../utils/QuantityInput";
import { devise } from "../utils/utils";

const Card = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { customerInfo } = useSelector((state) => state.auth);
  const {
    card_products,
    outOfStock_products,
    card_loading,
    deleteError,
    price,
    shipping_fee,
    buy_products_total,
    deleteSuccess,
  } = useSelector((state) => state.card);
  const { categories, loading, errorMessage, clientLocation } = useCategories();

  const total = (parseFloat(price) + parseFloat(shipping_fee)).toFixed(2);

  const { t } = useTranslation();

  const redirectToShipping = () => {
    if (!card_products.length) {
      toast.error(t("Votre panier est vide"));
      return;
    }

    navigate("/shipping", {
      state: {
        products: card_products,
        price,
        Shipping_fees: shipping_fee,
        coupon: 0,
        items: buy_products_total,
      },
    });
  };

  useEffect(() => {
    if (deleteError) {
      toast.error(deleteError);
      dispatch(messageClear());
    }
    if (deleteSuccess) {
      toast.success(deleteSuccess);
      dispatch(messageClear());
      dispatch(get_card_products(customerInfo?.id));
    }
  }, [deleteError, deleteSuccess, dispatch, customerInfo?.id]);

  useEffect(() => {
    if (customerInfo?.id && customerInfo.id.length === 24) {
      dispatch(get_card_products(customerInfo.id));
    }
  }, [customerInfo?.id, dispatch]);

  return (
    <div className='font-helvetica select-none'>
      <Headers categories={categories} clientLocation={clientLocation} />
      {(card_loading || loading) && (
        <div className='w-screen h-screen flex justify-center items-center fixed left-0 top-0 bg-gray-500/20 z-[99999]'>
          <FadeLoader />
        </div>
      )}
      {errorMessage && (
        <div>
          {t("Error")}: {errorMessage}
        </div>
      )}
      <section
        style={{
          backgroundImage: `url('${process.env.REACT_APP_CLIENT_URL}/images/card/1.jpg')`,
        }}
        className='h-[120px] sm:h-[80px] mt-6 bg-no-repeat relative bg-left bg-cover select-none'
      >
        <div className='absolute left-0 top-0 w-full h-full bg-blue-800/30 flex flex-col justify-center items-center text-white'>
          <h1 className='text-3xl font-bold'>{t("My Shop")}</h1>
          <div className='flex gap-2 items-center text-2xl'>
            <Link to='/' className='hover:underline'>
              {t("Home")}
            </Link>
            <MdOutlineKeyboardArrowRight />
            <span>{t("Cart")}</span>
          </div>
        </div>
      </section>

      <section className='bg-[#eeeeee]'>
        <div className='w-[95%] lg:w-[90%] mx-auto py-16 sm:pt-4 '>
          {card_products?.length > 0 || outOfStock_products?.length > 0 ? (
            <div className='flex flex-wrap'>
              <div className='w-[67%] md-lg:w-full '>
                <div className='pr-3 md-lg:pr-0'>
                  <div className='flex flex-col gap-3'>
                    <div className='bg-white p-4'>
                      <h2 className='text-md font-bold text-green-600'>
                        {" "}
                        {t("In Stock")} : {buy_products_total}
                      </h2>
                    </div>
                    {/* Products In Stock */}
                    <div className=' md-lg:w-full md-lg:pr-0'>
                      {card_products.map((card, idx) => (
                        <div
                          key={idx}
                          className='bg-white flex flex-col gap-4 p-4'
                        >
                          <h2 className='font-bold text-green-600'>
                            {card.shopName}
                          </h2>
                          {card.products.map((product, index) => {
                            const variant = product.selectedVariant;
                            const displayImage =
                              variant?.variantImage ||
                              product.productInfo.images[0]?.url ||
                              product.productInfo.images[0];
                            const displayPrice = variant
                              ? (
                                  variant.variantPrice *
                                  (1 -
                                    (product.productInfo.discount || 0) / 100)
                                ).toFixed(2)
                              : product.productInfo.price;
                            const stock = variant
                              ? variant.variantStock
                              : product.productInfo.stock;

                            return (
                              <div
                                key={index}
                                className='flex justify-between gap-2 border-b pb-2 mb-2 sm:flex-col'
                              >
                                <div className='flex flex-wrap gap-2'>
                                  <Link
                                    to={`/product/details/${product.productId}`}
                                  >
                                    <img
                                      src={displayImage}
                                      alt={product.productInfo.name}
                                      className='h-[80px] w-auto object-contain'
                                    />
                                  </Link>
                                  <div className='flex-1'>
                                    <h2 className='text-sm font-semibold uppercase'>
                                      {product.productInfo.name}{" "}
                                      {variant && (
                                        <span className='text-blue-600'>
                                          [{variant.color} {variant.size}]
                                        </span>
                                      )}
                                    </h2>
                                    <span className='text-sm'>
                                      {product.productInfo.brand}
                                    </span>
                                    <p>Stock: {stock}</p>
                                    <p>
                                      {t("Delivery")}:{" "}
                                      {
                                        product.productInfo
                                          .estimatedDeliveryTime
                                      }
                                    </p>
                                  </div>
                                </div>
                                <div className='text-right sm:flex sm:flex-col sm:gap-3'>
                                  <div className='sm:flex sm:justify-between sm:items-center'>
                                    <h2 className='text-md text-orange-500 font-bold'>
                                      {displayPrice.toFixed(2)} {devise}
                                    </h2>
                                    {product.productInfo.discount > 0 && (
                                      <p className='line-through text-gray-400'>
                                        {variant
                                          ? variant.variantPrice
                                          : product.productInfo.price /
                                            (1 -
                                              (product.productInfo.discount ||
                                                0) /
                                                100)}{" "}
                                        {devise}
                                      </p>
                                    )}
                                  </div>
                                  <div className='sm:flex sm:justify-between sm:items-center'>
                                    <QuantityInput
                                      product={product}
                                      stockLimit={stock}
                                      fonction='card'
                                    />
                                    <button
                                      onClick={() =>
                                        dispatch(
                                          delete_card_product(product._id),
                                        )
                                      }
                                      className='mt-2 bg-red-500 text-white px-3 py-1 rounded'
                                    >
                                      {t("Delete")}
                                    </button>
                                  </div>
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      ))}
                    </div>

                    {outOfStock_products?.length > 0 && (
                      <div className='flex flex-col gap-3'>
                        <div className='bg-white p-4'>
                          <h2 className='text-md font-bold text-red-600'>
                            {t("Out of Stock")} : {outOfStock_products.length}
                          </h2>
                        </div>
                        <div className='bg-white p-4 '>
                          {outOfStock_products?.map((product, index) => {
                            const variant = product.selectedVariant;
                            const displayImage =
                              variant?.variantImage ||
                              product.productInfo?.images[0]?.url ||
                              product.productInfo?.images[0];
                            const displayPrice = (
                              product.productInfo?.price *
                              (1 - (product.productInfo?.discount || 0) / 100)
                            ).toFixed(2);
                            const stock = product.productInfo?.stock;

                            return (
                              <div
                                key={index}
                                className='flex flex-nowrap gap-2 border-b pb-2 mb-2 justify-between'
                              >
                                <Link
                                  to={`/product/details/${product.productId}`}
                                  className='flex gap-2 justify-center items-center relative'
                                >
                                  <img
                                    src={displayImage}
                                    alt={product.productInfo.name}
                                    className='h-[80px] w-auto object-contain'
                                  />

                                  <div className='absolute top-0 left-0 w-full h-full border-2 border-red-500 pointer-events-none'>
                                    <div className='w-full h-full flex justify-center items-center bg-red-500/10 text-blue-600 text-xs font-bold uppercase'>
                                      {t("Out of Stock")}
                                    </div>
                                  </div>
                                  <div className='pr-4 text-slate-600'>
                                    <h2 className='text-sm font-semibold text-gray-600 uppercase'>
                                      {product.productInfo.name}
                                    </h2>
                                    <span className='text-sm font-normal'>
                                      {product.productInfo.brand}
                                    </span>
                                    <p>Stock: {stock}</p>
                                    <p>
                                      {t("Delivery")}:{" "}
                                      {
                                        product.productInfo
                                          .estimatedDeliveryTime
                                      }
                                    </p>
                                  </div>
                                </Link>
                                <div className='text-right sm:'>
                                  <h2 className='text-lg text-orange-500 font-bold'>
                                    {displayPrice} {devise}
                                  </h2>
                                  {product.productInfo.discount > 0 && (
                                    <p className='line-through text-gray-400'>
                                      {variant
                                        ? variant.variantPrice
                                        : product.productInfo.price}{" "}
                                      {devise}
                                    </p>
                                  )}
                                  <QuantityInput
                                    product={product}
                                    stockLimit={stock}
                                    fonction='card'
                                  />
                                  <button
                                    onClick={() =>
                                      dispatch(delete_card_product(product._id))
                                    }
                                    className='mt-2 bg-red-500 text-white px-3 py-1 rounded'
                                  >
                                    {t("Delete")}
                                  </button>
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              <div className='w-[33%] md-lg:w-full'>
                <div className='pl-3 md-lg:pl-0 md-lg:mt-5 md-lg:mb-5'>
                  {card_products.length > 0 && (
                    <div className='bg-white text-slate-600 flex flex-col gap-3 p-3'>
                      <h2 className='text-xl  font-bold'>
                        {t("Order Summary")}
                      </h2>
                      <div className='flex justify-between items-center'>
                        <span>{t("Total Items")}</span>
                        <span>{buy_products_total}</span>
                      </div>
                      <div className='flex justify-between items-center'>
                        <span>{t("Sub Total")}</span>
                        <span>
                          {price} {devise}
                        </span>
                      </div>
                      <div className='flex justify-between items-center'>
                        <span>{t("Shipping Fees")}</span>
                        <span>
                          {shipping_fee} {devise}
                        </span>
                      </div>
                      <div className='flex gap-2 p-2'>
                        <input
                          type='text'
                          className='w-full px-3 border border-slate-500 outline-0 focus:border-green-500 rounded-sm'
                          placeholder={t("Input your coupon code")}
                        />
                        <button className='bg-orange-500 text-white px-5 py-[3px] rounded '>
                          {t("Apply")}
                        </button>
                      </div>
                      <div className='flex justify-between items-center'>
                        <span>Total</span>
                        <span className='text-lg text-orange-500 font-bold'>
                          {parseFloat(total).toFixed(2)} {devise}
                        </span>
                      </div>

                      <button
                        onClick={redirectToShipping}
                        className='w-full mt-3 bg-orange-500 text-white py-2 rounded'
                      >
                        {t("Proceed to Checkout")}
                      </button>
                    </div>
                  )}
                </div>
              </div>
            </div>
          ) : (
            <div className='flex flex-col text-center items-center justify-center gap-30'>
              <p className='text-gray-500 font-helvetica'>
                {t("Your cart is empty.")}
              </p>
              <Link
                to='/shops'
                className='px-4 py-2 bg-indigo-500 text-white rounded'
              >
                {t("Shop Now")}
              </Link>
            </div>
          )}
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default Card;
